import { getProductRepository } from '../repositories/factory';
import { Product } from '../types';
import { mapDbProductToFrontend } from '../utils/mappers';
import { supabase } from '../lib/supabase';

/**
 * Service Layer para lógica de negocio de productos.
 * 
 * Encapsula la lógica de negocio y coordina entre repositories.
 * Los componentes UI solo deben usar services, nunca repositories directamente.
 */
export class ProductService {
    private repo = getProductRepository();

    /**
     * Obtiene todos los productos
     */
    async getAllProducts(): Promise<Product[]> {
        return this.repo.getAll();
    }

    /**
     * Obtiene productos paginados con filtros
     */
    async getPaginatedProducts(
        page: number,
        limit: number,
        query?: string,
        filters?: { category?: string[]; make?: string[]; model?: string[] }
    ): Promise<{ data: Product[]; total: number }> {
        return this.repo.getPaginated(page, limit, query, filters);
    }

    /**
     * Alterna la visibilidad de un producto
     */
    async toggleProductVisibility(id: string, currentStatus: boolean): Promise<void> {
        return this.repo.toggleVisibility(id, currentStatus);
    }

    /**
     * Obtiene listas de filtros únicos
     * NOTA: Esto idealmente debería ser cacheado o venir de tablas maestras.
     */
    async getUniqueFilters(): Promise<{ categories: string[], makes: string[], models: string[] }> {
        // Fetch from Metadata tables to ensure we have ALL options, not just used ones
        const metadata = await this.repo.getMetadata();

        return {
            categories: metadata.categories,
            makes: metadata.brands,
            models: metadata.models
        };
    }

    /**
     * Obtiene un producto por ID
     */
    async getProductById(id: string): Promise<Product | null> {
        return this.repo.getById(id);
    }

    /**
     * Obtiene productos de una marca específica
     */
    async getProductsByBrand(brandSlug: string): Promise<Product[]> {
        return this.repo.getByBrand(brandSlug);
    }

    /**
     * Obtiene productos de una categoría específica
     */
    async getProductsByCategory(categorySlug: string): Promise<Product[]> {
        return this.repo.getByCategory(categorySlug);
    }

    /**
     * Busca productos por query
     */
    async searchProducts(query: string): Promise<Product[]> {
        if (!query.trim()) {
            return this.getAllProducts();
        }
        return this.repo.search(query);
    }

    /**
     * Obtiene productos disponibles en una sucursal
     */
    async getAvailableProducts(branchId: string): Promise<Product[]> {
        return this.repo.getAvailableInBranch(branchId);
    }

    /**
     * Filtra productos con múltiples criterios
     */
    async filterProducts(filters: {
        brand?: string;
        category?: string;
        branchId?: string;
        inStockOnly?: boolean;
        searchQuery?: string;
    }): Promise<Product[]> {
        let products: Product[];

        // Si hay búsqueda, empezar con eso
        if (filters.searchQuery) {
            products = await this.searchProducts(filters.searchQuery);
        } else {
            products = await this.getAllProducts();
        }

        // Aplicar filtros
        if (filters.brand) {
            products = products.filter(p =>
                p.make?.toLowerCase() === filters.brand!.toLowerCase()
            );
        }

        if (filters.category) {
            products = products.filter(p =>
                p.category?.toLowerCase() === filters.category!.toLowerCase()
            );
        }

        if (filters.branchId && filters.inStockOnly) {
            products = products.filter(p => p.stock?.[filters.branchId!]);
        }

        return products;
    }

    /**
     * Actualiza el stock de un producto en una sucursal
     * (Útil para panel admin futuro)
     */
    async updateProductStock(
        productId: string,
        branchId: string,
        inStock: boolean
    ): Promise<void> {
        return this.repo.updateStock(productId, branchId, inStock);
    }

    /**
     * Verifica en tiempo real en la BD si el producto sigue en stock 
     * antes de enviarlo por WhatsApp (Patrón JIT).
     */
    async checkRealtimeAvailability(productId: string, variantId?: string, modelId?: string): Promise<boolean> {
        return this.repo.checkRealtimeAvailability(productId, variantId, modelId);
    }
    /**
     * Actualiza los metadatos de un producto
     */
    async updateProduct(id: string, updates: Partial<Product>): Promise<Product> {
        return this.repo.update(id, updates);
    }

    /**
     * Crea un nuevo producto
     */
    async createProduct(product: Omit<Product, 'id' | 'created_at'>): Promise<Product> {
        return this.repo.create(product);
    }

    /**
     * Elimina un producto por ID
     */
    async deleteProduct(id: string): Promise<void> {
        return this.repo.delete(id);
    }

    /**
     * Suscribe a cambios en la tabla 'products' de Supabase
     * Retorna una función para cancelar la suscripción.
     * @param filter Filtro opcional (ej: 'is_active=eq.true'). Obligatorio para usuarios no autenticados debido a RLS.
     */
    subscribeToProducts(callback: (product: Product, eventType: string) => void, filter?: string) {
        // Use a unique name with a prefix to distinguish subscriptions
        const subType = filter ? 'pub' : 'adm';
        const channelId = `products-${subType}-${Math.random().toString(36).substring(2, 7)}`;

        const channel = supabase
            .channel(channelId)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'products',
                    filter: filter
                },
                (payload: any) => {
                    // eventType can be 'INSERT', 'UPDATE', 'DELETE'
                    const eventType = payload.eventType;

                    // For DELETE, new is empty, so we send the old ID
                    const productData = (eventType === 'DELETE') ? payload.old : payload.new;

                    if (productData) {
                        if (eventType === 'UPDATE') {
                            // Send raw partial to prevent destroying joined variants
                            callback(productData as any, eventType);
                        } else {
                            const product = mapDbProductToFrontend(productData);
                            callback(product, eventType);
                        }
                    }
                }
            )
            .subscribe((status: string, err?: any) => {
                if (status === 'SUBSCRIBED') {
                    if (import.meta.env.DEV) console.log(`[Realtime] ✅ ${channelId} (${filter || 'Admin Mode'})`);
                }
                if (status === 'CHANNEL_ERROR') {
                    if (import.meta.env.DEV && err) {
                        console.warn(`[Realtime] ⚠️ ${channelId} (Status: ${status}):`, err);
                    }
                }
            });

        return () => {
            if (import.meta.env.DEV) console.log(`[Realtime] 🗑️ Removing ${channelId}`);
            supabase.removeChannel(channel);
        };
    }
}

// Singleton export
export const productService = new ProductService();
